import {
  Enum_Subscription_Status,
  useGetCompanyInfoLiteQuery,
  useMeQuery
} from '../generated/graphql'
import { hasAccessWindowLapsed, hasActiveSubscription } from '../utils/subscriptionAccess'

const useIsSubscribed = () => {
  const { data: company, loading } = useGetCompanyInfoLiteQuery()
  const { data: user } = useMeQuery()

  const subscriptionStatus = company?.getCompanyInfo?.paymentMethod
    ? company?.getCompanyInfo?.subscription?.data?.attributes?.status || 'not subscribed'
    : Enum_Subscription_Status.Incomplete
  const isManual =
    company?.getCompanyInfo?.subscription?.data?.attributes?.product_plan?.data?.attributes
      ?.isManual || false
  const planName =
    company?.getCompanyInfo?.subscription?.data?.attributes?.product_plan?.data?.attributes?.name ||
    undefined
  const planId =
    company?.getCompanyInfo?.subscription?.data?.attributes?.product_plan?.data?.id || undefined
  const subscriptionAttributes = company?.getCompanyInfo?.subscription?.data?.attributes

  // Evaluate access with the SAME rules the backend policy uses, so the UI never
  // renders a working app whose every mutation the server will reject. Reading
  // status alone treated a lapsed trial as live, which surfaced to users as
  // unexplained "Your subscription is inactive" errors.
  const accessInput = {
    paymentMethod: company?.getCompanyInfo?.paymentMethod,
    subscription: subscriptionAttributes
      ? {
          status: subscriptionAttributes.status,
          trial_end: subscriptionAttributes.trial_end,
          cancel_at: subscriptionAttributes.cancel_at,
          current_period_end: subscriptionAttributes.current_period_end,
          cancel_at_period_end: subscriptionAttributes.cancel_at_period_end,
          isManual
        }
      : null
  }

  return {
    planName,
    planId,
    isStarterPlan: (planName || '').trim().toLowerCase().includes('starter'),
    isSubscribed: hasActiveSubscription(accessInput),
    // True once the trial/cancelled period has actually elapsed. Lets callers
    // distinguish "never subscribed" from "expired" and prompt to reactivate.
    accessLapsed: hasAccessWindowLapsed(accessInput.subscription),
    subscriptionStatus,
    loading,
    isManual,
    billingInformationCompleted:
      user?.myUser?.attributes?.onboardingInformation?.billingInformationCompleted,
    // The currency the customer chose at signup (lowercase ISO: 'gbp'|'usd'|'zar').
    currency: company?.getCompanyInfo?.subscription?.data?.attributes?.currency || undefined,
    cancelAtPeriodEnd:
      company?.getCompanyInfo?.subscription?.data?.attributes?.cancel_at_period_end,
    // When the free trial ends (the first charge, for a trial with a card).
    trialEndsAt: company?.getCompanyInfo?.subscription?.data?.attributes?.trial_end,
    // A real saved card — not missing, and not the no-card trial sentinel.
    hasCardOnFile: Boolean(
      company?.getCompanyInfo?.paymentMethod &&
      company.getCompanyInfo.paymentMethod !== 'FREE_TRIAL'
    ),
    // When access actually ends. `cancel_at` is set once a cancellation is
    // scheduled; otherwise fall back to the period/trial end we already hold.
    accessEndsAt:
      company?.getCompanyInfo?.subscription?.data?.attributes?.cancel_at ||
      company?.getCompanyInfo?.subscription?.data?.attributes?.current_period_end ||
      company?.getCompanyInfo?.subscription?.data?.attributes?.trial_end,
    memberLimit:
      company?.getCompanyInfo?.subscription?.data?.attributes?.product_plan?.data?.attributes
        ?.ProductFeature?.baoriders_allowed,
    extraAmount:
      company?.getCompanyInfo?.subscription?.data?.attributes?.product_plan?.data?.attributes
        ?.ProductFeature?.cost_per_baorider,
    projectCreated: user?.myUser?.attributes?.onboardingInformation?.projectCreated
  }
}

export default useIsSubscribed
